import { useEffect, useRef, useMemo, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import PostCard from "../PostCard/PostCard";
import CreatePostBox from "../CreatePostBox/CreatePostBox";
import usePosts from "../../../hooks/usePosts";
import { selectEffectiveUserId } from "../../../../sous-profile/redux/selectors/sousProfileSelectors";
import "./MainFeed.css";

const getPostKey = (post, index) =>
  post.postId || post._id || post.id || post.id_post || `post-${index}`;

export default function MainFeed() {
  const {
    loading,
    createLoading,
    error,
    hasPosts,
    handleFetchPosts,
    handleRefreshPosts,
    handleEditPost,
    handleDeletePost,
    handleLoadMore,
    displayedPosts,
    hasMore,
    loadingMore,
  } = usePosts();

  const userId = useSelector(selectEffectiveUserId);

  // ✅ FIX 1: liste locale pour insertion optimiste immédiate
  const [optimisticPosts, setOptimisticPosts] = useState([]);
  // IDs des posts optimistes déjà remplacés par la vraie donnée API
  const optimisticIdsRef = useRef(new Set());

  const sentinelRef = useRef(null);

  useEffect(() => {
    handleFetchPosts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ FIX 2: quand Redux se met à jour, retirer les posts optimistes
  // dont on a maintenant la vraie version côté serveur
  useEffect(() => {
    if (!displayedPosts?.length) return;
    setOptimisticPosts((prev) => {
      if (!prev.length) return prev;
      // Garde uniquement les optimistes dont l'id temp n'est pas encore dans Redux
      return prev.filter((op) => {
        const opId = op.id_post || op.postId || op.id;
        const isTemp = String(opId).startsWith("temp_");
        if (!isTemp) return false; // déjà remplacé
        // Vérifie si Redux a un post avec la même description publiée par le même user
        const found = displayedPosts.some(
          (p) =>
            p.description === op.description &&
            (String(p.userId) === String(op.userId) ||
              String(p.user?.id) === String(op.userId))
        );
        return !found; // garde l'optimiste seulement si pas encore dans Redux
      });
    });
  }, [displayedPosts]);

  // ✅ FIX 3: callback appelé par CreatePostBox dès la publication réussie
  const handlePostCreated = useCallback(
    (newPost) => {
      setOptimisticPosts((prev) => {
        // Évite les doublons
        const exists = prev.some(
          (p) =>
            (p.id_post && p.id_post === newPost.id_post) ||
            (p.postId && p.postId === newPost.postId)
        );
        if (exists) return prev;
        return [newPost, ...prev];
      });

      // Re-fetch silencieux en arrière-plan après 2s
      // pour remplacer le post optimiste par la vraie donnée serveur
      setTimeout(() => {
        handleFetchPosts().catch(() => {});
      }, 2000);
    },
    [handleFetchPosts]
  );

  // ✅ FIX 4: fusionner optimistes + Redux sans doublons
  const mergedPosts = useMemo(() => {
    const reduxPosts = displayedPosts || [];
    if (!optimisticPosts.length) return reduxPosts;

    // Les posts optimistes vont EN TÊTE
    // On retire de Redux les posts qui correspondent déjà à un optimiste
    const filteredRedux = reduxPosts.filter((rp) => {
      return !optimisticPosts.some(
        (op) =>
          op.description === rp.description &&
          (String(rp.userId) === String(op.userId) ||
            String(rp.user?.id) === String(op.userId))
      );
    });

    return [...optimisticPosts, ...filteredRedux];
  }, [optimisticPosts, displayedPosts]);

  // IntersectionObserver pour infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, handleLoadMore]);

  const LoadingSpinner = useCallback(
    () => (
      <div className="feed-loading">
        <div className="loading-spinner">⏳</div>
        <p>Chargement des posts...</p>
      </div>
    ),
    []
  );

  const ErrorMessage = useCallback(
    ({ error, onRetry }) => (
      <div className="feed-error">
        <div className="error-icon">❌</div>
        <div className="error-text">Erreur: {error}</div>
        <button onClick={onRetry} className="retry-button">
          🔄 Réessayer
        </button>
      </div>
    ),
    []
  );

  const EmptyState = useCallback(
    () => (
      <div className="feed-empty">
        <div className="empty-icon">📭</div>
        <h3>Aucun post publié</h3>
        <p>Soyez le premier à partager quelque chose !</p>
        <button onClick={handleRefreshPosts} className="refresh-button">
          🔄 Actualiser
        </button>
      </div>
    ),
    [handleRefreshPosts]
  );

  // ✅ hasPosts tient compte aussi des posts optimistes
  const hasAnyPost = hasPosts || optimisticPosts.length > 0;

  return (
    <div className="main-feed">
      {/* ✅ FIX: passe onPostCreated à CreatePostBox */}
      <CreatePostBox onPostCreated={handlePostCreated} />

      {error && <ErrorMessage error={error} onRetry={handleFetchPosts} />}

      {loading && !hasAnyPost ? (
        <LoadingSpinner />
      ) : hasAnyPost ? (
        <div className="posts-container">
          {mergedPosts.map((post, index) => (
            <PostCard
              key={getPostKey(post, index)}
              post={post}
              currentUserId={userId}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
            />
          ))}

          <div ref={sentinelRef} className="scroll-sentinel" />

          {loadingMore && (
            <div className="feed-loading-more">
              <div className="loading-circle" />
            </div>
          )}

          {!hasMore && mergedPosts.length > 0 && (
            <div className="feed-end" />
          )}
        </div>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}
